import type { SelectOption } from '../../shared/components/select/select';

/**
 * SEARCHABLE CLAIM DROPDOWNS — shared rules
 *
 * 1) User searched and API returned zero rows while a value was selected → clear that selection
 *    and show an empty list (caller refetches / user types again).
 *
 * 2) No selection OR the user has a non-empty search query → apply full API `mapped` (first page
 *    or search hits). If the selected id is not in `mapped`, `ensureSelectedInOptions` appends
 *    one synthetic row so the chip/label still shows (selected + search results).
 *
 * 3) A value is selected AND the request had no search query (re-open dropdown, initial “empty”
 *    load) → do **not** show the first API page; options start empty, then only the selected
 *    value is injected via `ensureSelectedInOptions` (needs `displayName` from the claim/API).
 *    This avoids “first 10 + selected duplicated at end” when the pick is outside page 1.
 *
 * **Currency (credit note) exception:** use a dedicated field (e.g. `creditNoteCurrencyId`) for
 * `selectedId` so it is unset on first open; then branch (2) runs and the first 10 load. Line /
 * header `currency` must not be passed as `selectedId` for that control.
 *
 * **Empty search refetch:** callers should still call the API with `{ page: 1, limit: N }` when
 * the user clears the query (and often skip the call when empty + selected — see country tab).
 */

/**
 * Standard handling for searchable dropdown API responses.
 * @see module comment above for branch behaviour.
 */
export function processSearchResponse(
  mapped: SelectOption[],
  searchQuery: string | undefined,
  selectedId: number | string | undefined | null,
  displayName: string | undefined | null,
  optionsSet: (opts: SelectOption[]) => void,
  optionsUpdate: (updater: (prev: SelectOption[]) => SelectOption[]) => void,
  onClearNoResults: () => void
): void {
  let optionsToSet: SelectOption[];
  const hasSearchQuery = !!searchQuery;
  if (hasSearchQuery && mapped.length === 0 && selectedId != null) {
    optionsToSet = [];
    optionsSet(optionsToSet);
    onClearNoResults();
  } else if (!selectedId || hasSearchQuery) {
    optionsToSet = mapped;
    optionsSet(optionsToSet);
  } else {
    // Selected + no search: show only selection (synthetic row), not first page + append
    optionsToSet = [];
    optionsSet(optionsToSet);
  }
  const isClearBranch = hasSearchQuery && mapped.length === 0 && selectedId != null;
  if (selectedId != null && !isClearBranch) {
    ensureSelectedInOptions(optionsToSet, selectedId, displayName, optionsUpdate);
  }
}

/**
 * When a claim has a selected value from API (edit mode), ensure it exists in the options
 * so the select can display it. If missing, adds the selected item using the display name.
 */
export function ensureSelectedInOptions(
  options: SelectOption[],
  selectedId: number | string | undefined | null,
  displayName: string | undefined | null,
  optionsUpdate: (updater: (prev: SelectOption[]) => SelectOption[]) => void
): void {
  if (selectedId == null) return;
  const has = options.some(o => o.value === selectedId || Number(o.value) === Number(selectedId));
  if (!has && displayName) {
    optionsUpdate(prev => [...prev, { value: selectedId, label: displayName }]);
  }
}
